import React, { useCallback, useReducer } from 'react';
import FormControl from '@mui/material/FormControl';
import { makeStyles } from '@mui/styles';
import { useMutation, useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import DropdownFormField from 'components/common/DropdownFormField';
import FormField from 'components/common/FormField';
import PhoneNumberFormField from 'components/common/PhoneNumberFormField';
import TextFormField from 'components/common/TextFormField';
import { BusinessPicker } from 'components/common/businessPicker/BusinessPicker';
// Components
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { USA_STATE_LIST } from 'utils/constants';
import { getIndividualBusiness } from 'api/admin/businesses/businesses';
import './CreateNewStore.scss';
import createStore from './mutations/createStore';
import useDistricts from './queries/useDistricts';
import useTaxes from './queries/useTaxes';

const states = USA_STATE_LIST.map((state) => ({ value: state, display: state }));

const SMALL_LOADER_SIZE = 20;

const inputProps = {
  name: { maxLength: 50 },
  address: { maxLength: 35 },
  zip: { maxLength: 10 },
  dcaNumber: { maxLength: 15 },
  password: { minLength: 8 },
  requiredDropdown: { required: true },
};

const useStyles = makeStyles(() => ({
  formControl: {
    width: '65%',
    margin: 0,
  },
  inputRoot: {
    width: '65%',
    margin: 0,
  },
}));

const initialState = {
  businessId: null,
  name: '',
  address: '',
  city: '',
  state: null,
  zipCode: '',
  districtId: null,
  phoneNumber: '',
  dcaRetail: '',
  dcaCommercial: '',
  taxRateId: null,
  password: '',
  confirmPassword: '',
};

const ACTIONS = {
  CHANGE_FIELD: 'CHANGE_FIELD',
};

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.CHANGE_FIELD: {
      const { value, field } = action;
      let newValue = value;

      if (field === 'password' || field === 'confirmPassword') {
        newValue = newValue.trim();
      }

      const newState = {
        ...state,
        [field]: newValue,
      };

      if (field === 'businessId') {
        newState.districtId = null;
        newState.taxRateId = null;
      }

      return newState;
    }
    default:
      return state;
  }
}

const CreateNewStore = () => {
  const [store, dispatch] = useReducer(reducer, initialState);

  const { isLoading: isTaxesLoading, error: taxesError, data: taxes } = useTaxes(store.businessId);
  const {
    isLoading: isDistrictsLoading,
    error: districtsError,
    data: districts,
  } = useDistricts(store.businessId);

  // Fetch the selected business to determine needsRegions (controls whether the District
  // field is shown). BusinessPicker owns the search/display; we only need this one flag.
  const { data: selectedBusinessData } = useQuery(
    ['individualBusiness', store.businessId],
    async () => {
      const { data } = await getIndividualBusiness(store.businessId);
      return data.business;
    },
    { enabled: !!store.businessId, staleTime: 5 * 60 * 1000 },
  );

  const history = useHistory();

  const materialClasses = useStyles();

  const {
    isLoading: requestLoading,
    error: mutationError,
    mutate: createNewStore,
  } = useMutation({
    mutationFn: createStore,
  });

  const handleFieldChange = useCallback((value, field) => {
    dispatch({ type: ACTIONS.CHANGE_FIELD, field, value });
  }, []);

  const navigateToStore = (id) => {
    history.push(`/stores/${id}`);
  };

  const needsRegions = !!selectedBusinessData?.needsRegions;

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    await createNewStore({
      ...store,
      needsRegions,
      onSave: (id) => navigateToStore(id),
    });
  };

  const requestError = mutationError?.response?.data?.error ?? mutationError?.message ?? null;
  const error = taxesError ?? districtsError ?? requestError;

  const smallLoader = <CentsLoader height={SMALL_LOADER_SIZE} width={SMALL_LOADER_SIZE} />;

  const isSubmitDisabled = isTaxesLoading || isDistrictsLoading;

  return (
    <>
      <div className="create-new-user-container create-new-store">
        <div className="create-new-user-form-container">
          <form className="create-new-user-form" onSubmit={handleFormSubmit}>
            <h1>Store Information</h1>
            <FormField label="Business">
              <FormControl variant="outlined">
                <BusinessPicker
                  selectedBusinessId={store.businessId}
                  onChange={(businessId) => handleFieldChange(businessId, 'businessId')}
                />
              </FormControl>
            </FormField>
            <TextFormField
              label="Location Name"
              name="name"
              onChange={handleFieldChange}
              value={store.name}
              inputProps={inputProps.name}
              required
              className={materialClasses.inputRoot}
            />
            <TextFormField
              label="Address"
              name="address"
              onChange={handleFieldChange}
              value={store.address}
              inputProps={inputProps.address}
              required
              className={materialClasses.inputRoot}
            />
            <TextFormField
              label="City"
              name="city"
              onChange={handleFieldChange}
              value={store.city}
              required
              className={materialClasses.inputRoot}
            />
            <DropdownFormField
              value={store.state}
              name="state"
              onChange={handleFieldChange}
              label="State"
              options={states}
              inputProps={inputProps.requiredDropdown}
              className={materialClasses.formControl}
            />
            <TextFormField
              label="Zip"
              name="zipCode"
              onChange={handleFieldChange}
              value={store.zipCode}
              inputProps={inputProps.zip}
              required
              className={materialClasses.inputRoot}
            />
            {needsRegions ? (
              !isDistrictsLoading ? (
                <DropdownFormField
                  key={`districts_${store.businessId}`}
                  value={store.districtId}
                  name="districtId"
                  onChange={handleFieldChange}
                  label="District"
                  options={districts}
                  inputProps={inputProps.requiredDropdown}
                  className={materialClasses.formControl}
                />
              ) : (
                smallLoader
              )
            ) : null}
            <PhoneNumberFormField
              label="Phone"
              name="phoneNumber"
              onChange={handleFieldChange}
              value={store.phoneNumber}
              required
              className={materialClasses.inputRoot}
            />
            <TextFormField
              label="DCA Number - Retail"
              name="dcaRetail"
              onChange={handleFieldChange}
              value={store.dcaRetail}
              inputProps={inputProps.dcaNumber}
              className={materialClasses.inputRoot}
            />
            <TextFormField
              label="DCA Number - Commercial"
              name="dcaCommercial"
              onChange={handleFieldChange}
              value={store.dcaCommercial}
              inputProps={inputProps.dcaNumber}
              className={materialClasses.inputRoot}
            />
            {!isTaxesLoading ? (
              <DropdownFormField
                key={`taxes_${store.businessId}`}
                value={store.taxRateId}
                name="taxRateId"
                onChange={handleFieldChange}
                label="Tax Rate"
                options={taxes}
                className={materialClasses.formControl}
              />
            ) : (
              smallLoader
            )}
            <TextFormField
              label="New Password"
              name="password"
              onChange={handleFieldChange}
              value={store.password}
              type="password"
              inputProps={inputProps.password}
              required
              className={materialClasses.inputRoot}
            />
            <TextFormField
              label="Confirm Password"
              name="confirmPassword"
              onChange={handleFieldChange}
              value={store.confirmPassword}
              type="password"
              inputProps={inputProps.password}
              required
              className={materialClasses.inputRoot}
            />
            <div>
              <p>
                <b>* </b>
                <i>Password must have at least one letter, one number and one special character.</i>
              </p>
            </div>
            <div className="submit-button-container">
              {!requestLoading ? (
                <button
                  className={classNames('submit-button', {
                    active: !isSubmitDisabled,
                    disabled: isSubmitDisabled,
                  })}
                  disabled={isSubmitDisabled}
                >
                  Create
                </button>
              ) : (
                <CentsLoader />
              )}
              {error ? <p className="error-text">Error: {error}</p> : null}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateNewStore;
